// the simple interaction event/widget editing view which corresponds to CS1Surveys
// MVP - compatible

import React from "react";
import TextField from "@mui/material/TextField";
import Interaction from "../../../../../models/interactions/Interaction";
import InteractionSettings from "../../../../../models/config/InteractionSettings";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import IconButton from "@mui/material/IconButton";
import V1SurveySearch from "./external/V1SurveySearch";
import { useEffect, useState } from "react";
import { navSliceActions } from "../../../../../store/slices/NavSlice";
import { useRowSelector, useStoreDispatch, useStoreSelector } from "../../../../../store/Hooks";
import { interactionTreeSliceActions } from "../../../../../store/slices/InteractionTreeSlice";
import useHttp from "../../../../../hooks/useHttp";
import InteractionNode from "../../../../../models/interactions/InteractionNode";
import Interval from "../../../../../models/communications/Interval";
import InteractionNodeInterval from "../../../../../models/interactions/InteractionNodeInterval";
import Switch from '@mui/material/Switch';
import { enqueueSnackbar } from "notistack";
import  { InteractionInstanceProperties } from "../../../../../models/interactions/InteractionInstanceProperty";
import EditorWrapper from "../../../../ui/EditorWrapper";


const testing = process.env.REACT_APP_TEST_MODE === "test";


let lastMessage: any = null;

interface CS1SurveyProps {
  interaction: Interaction;
  handleCS1SurveyMapping: (value: any) => void;
  handleCS1NameMapping: (value: any) => void;
  interactionNode: InteractionNode | undefined;
  associatedIntervals: string | undefined;
  setInteractionInstanceProperties: (value: any) => void | undefined;
  interactionInstanceProperties: InteractionInstanceProperties | undefined;
  defaultInteractionInstanceProperties: InteractionInstanceProperties | undefined;
}

const CS1Survey = (props: CS1SurveyProps) => {
  const dispatch = useStoreDispatch();
  const [openSurveySearch, setOpenSurveySearch] = useState(true);

  const { sendRequest: fetchData } = useHttp();

  let thisNode: InteractionNode | null = null;

  if (props.interactionNode) {
    thisNode = props.interactionNode;
  }

  let surveyName: string | undefined;
  if (
    Object.prototype.hasOwnProperty.call(
      props.interaction.properties,
      "cacheFile",
    )
  ) {
    surveyName = JSON.stringify(props.interaction.properties);
    const surveySettings: InteractionSettings = JSON.parse(surveyName);
    surveyName = surveySettings.cacheFile;
  }
  const [surveyMappingName, setSurveyMappingName] = useState(surveyName);

  // we only update redux here, because we dont want to commit new interactionInstanceProperties to db
  // until the SAVE button in the editor is actually clicked by the user
  const handleInstancePropertySwitch = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const isSwitchOn = event.target.checked;
    if (isSwitchOn) {
      const interactionInstancePropertyCreated = props.defaultInteractionInstanceProperties;
      if (interactionInstancePropertyCreated) {
        interactionInstancePropertyCreated[type as keyof InteractionInstanceProperties] = true;
        props.setInteractionInstanceProperties(interactionInstancePropertyCreated);
      }
    } else {
      const interactionInstancePropertyDeleted = props.defaultInteractionInstanceProperties;
      if (interactionInstancePropertyDeleted) {
        interactionInstancePropertyDeleted[type as keyof InteractionInstanceProperties] = false;
        props.setInteractionInstanceProperties(interactionInstancePropertyDeleted)
      }
    }
  };

  useEffect(() => {
    window.addEventListener("message", async function (e) {
      // Get the sent data
      const data = e.data;
      if (lastMessage != data) {
        lastMessage = data
      } else {
        return
      }
      console.log(data)

      if (typeof data == 'object') {
        if (data.interaction) {
          Interaction.fromJSON(data.interaction);
        }

        if (data.interaction_node) {
          InteractionNode.fromJSON(data.interaction_node)

          // handle interaction node intervals
          // all checkboxes should be checked by default in UI
          // user can then "agree" with this default config by saving
          props.associatedIntervals?.split(',').forEach(async (interval)=>{
            const insertedInteractionNodeInterval = await InteractionNodeInterval.createInstance({
              interaction_node_id: data.interaction_node.id,
              interval_id: parseInt(interval),
              target:"subject"
            })
            // for now, committing INI's requires user "save"
            // insertedInteractionNodeInterval.save(); 
          })
        }

        dispatch(navSliceActions.closeRightDrawer({}));
      } else if (typeof data == "string") {
        if (data == "EXIT V1 INTERACTION SCREEN WITHOUT SAVING") {
          dispatch(navSliceActions.closeRightDrawer({}));
        }
        // if there's an error, grab the error and show it in v2
        if (data.includes("error")) {
          dispatch(navSliceActions.closeRightDrawer({}));
          const cleanedErrorMessage = data.replace("error", "");
          enqueueSnackbar(cleanedErrorMessage, { variant: "error" });
        }
      }
    });

  }, []);


  // the cs1survey event type is very simple, and only needs to receive 1 setting right now: the mapped PRO
  return (
    <div >
      {/* <div style={{ padding: "10px"}}>
        <TextField
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            props.handleCS1NameMapping(event.target.value);
          }}
          sx={{ width: "99%", opacity:props.interaction.id !== -1 ? 1 : 0 }}
          id="outlined-required"
          // label="Title"
          defaultValue={props.interaction.title}
          type = {props.interaction.id !== -1 ? "" : "hidden"}

        />
      </div> */}
      <div style={{ padding: "10px", marginTop: "20px" }}>
        {!testing && <TextField
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            props.handleCS1SurveyMapping(event.target.value);
          }}
          sx={{ width: "100%", opacity: props.interaction.id !== -1 ? 1 : 0 }}
          id="outlined-required"
          label="Mapped Survey"
          value={surveyMappingName}
          disabled
          // dont hide this if interaction already exists and we're just opening the editor
          // @TODO - probably just move delete buttons to be on the actual accordion itself, and then we dont need to have the edit button for CS1 surveys either
          // OR do we still have the edit button but we open the cs1 editor directly to that survey? (but limit them to that survey)
          type={props.interaction.id !== -1 ? "" : "hidden"}

        />}

        {/* FIXME */}
        {/* display interaction instance fields */}
        {/* no controls wired up yet since all cs1surveys will have same intstance props for now*/}
        {!testing && props?.interactionInstanceProperties && <div style={{ marginLeft: "10px" }}>
          <h3>Instance Properties</h3>
          <ul style={{ listStyleType: 'none' }}>
            {Object.keys(props?.interactionInstanceProperties).map((key: string) => (
              <li key={key}>
                <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                <Switch checked={props.interactionInstanceProperties && props.interactionInstanceProperties[key as keyof InteractionInstanceProperties]} inputProps={{ 'aria-label': 'controlled' }} onChange={(event: React.ChangeEvent<HTMLInputElement>) => { handleInstancePropertySwitch(event, key) }} />
              </li>
            ))}
          </ul>
        </div>}

        {/* FIXME */}
        {/* display interaction instance fields */}
        {/* no controls wired up yet since all cs1surveys will have same intstance props for now*/}
        {!testing && props.interaction.properties.instanceFields && props.interaction.properties.instanceFields.length > 0 && <div style={{ marginLeft: "10px" }}>
          <h3>Instance ID Fields</h3>
          <ul style={{ listStyleType: "none" }}>
            {props.interaction.properties.instanceFields.map((instanceField: string) => {
              return (<li key={instanceField}><span>{instanceField.charAt(0).toUpperCase() + instanceField.slice(1)}</span><Switch defaultChecked disabled /></li>)
            })}</ul>
        </div>}
        {/* <IconButton
          onClick={() => {
            setOpenSurveySearch(true);
          }}
          style={{ width: "10%", marginLeft: "10px" }}
          color="primary"
        >
          <ManageSearchIcon style={{ fontSize: "30pt" }} />
        </IconButton> */}
        {!testing && props.interaction.id === -1 &&
          <V1SurveySearch
            interaction={props.interaction}
            interactionNode={props.interactionNode}
            associatedIntervals={props.associatedIntervals}
            hide={!openSurveySearch}
          />
        }
        {testing && <EditorWrapper 
          inDrawer={true}
          title="Test CS1Survey Interaction"
          save={async ()=>{
            if(props.interaction && props.interactionNode){
              await Interaction.addNewInteraction(
                props.interactionNode.set_interaction_id,
                props.interactionNode.parent_interaction_id,
                'cs1survey',
                props.interactionNode.ordinal || 1,
                false)
            }
          }}
        
        >
          
          </EditorWrapper>}
      </div>
    </div>
  );
};

export default CS1Survey;
